import Routes from "@navigations/Routes";
import { isEmbeddedFrame, postIframeLayout } from "@utils/iframeMessaging";
import { isInactiveFrame } from "./contentBounds";

const MIN_CHANGE = 4;
const CACHE_KEY = "vdb_iframe_home_height";
const FIRST_POST_TIMEOUT_MS = 3000;

const reportedFrameHeight = (data: unknown) => {
  if (!Array.isArray(data) || data[0] !== "setIframeHeight") return 0;
  const height = Number(data[1]);
  return Number.isFinite(height) && height > 0 ? Math.ceil(height) : 0;
};

// The landing page reports its height once after it renders. Returning to
// Home does not trigger a new report, and a fresh load reports late, so keep
// the last value to size the frame right away.
// The landing page reflows with width, so a height is only valid at the width
// it was reported for.
const frameKey = (frame: HTMLIFrameElement) => {
  const width = Math.round(frame.getBoundingClientRect().width);
  try {
    const url = new URL(frame.src);
    return `${CACHE_KEY}:${url.origin}${url.pathname}:${width}`;
  } catch {
    return `${CACHE_KEY}:${width}`;
  }
};

const readCachedHeight = (frame: HTMLIFrameElement) => {
  try {
    const height = Number(window.localStorage.getItem(frameKey(frame)));
    return Number.isFinite(height) && height > 0 ? height : 0;
  } catch {
    return 0;
  }
};

const writeCachedHeight = (frame: HTMLIFrameElement, height: number) => {
  try {
    window.localStorage.setItem(frameKey(frame), String(height));
  } catch {
    // Storage can be blocked for third-party frames.
  }
};

const findActiveFrame = () =>
  Array.from(document.querySelectorAll("iframe")).find(
    (node) => !isInactiveFrame(node) && node.getBoundingClientRect().width > 0
  );

// The Home landing page is a nested, cross-origin document. It reports its own
// content height; this screen turns that into the outer iframe height.
export const initializeHomeHeight = () => {
  if (!isEmbeddedFrame()) return () => undefined;

  let active = true;
  let lastPosted = 0;
  let nestedHeight = 0;
  let frameId = 0;
  const startedAt = Date.now();

  const publish = () => {
    if (!active) return false;
    const frame = findActiveFrame();
    if (!frame) return false;
    if (nestedHeight <= 0) nestedHeight = readCachedHeight(frame);
    if (nestedHeight <= 0) return false;
    const rect = frame.getBoundingClientRect();
    const chromeBelow = Math.max(
      0,
      window.innerHeight - (rect.top + frame.clientHeight)
    );
    const height = Math.ceil(
      Math.max(0, rect.top) + nestedHeight + chromeBelow
    );
    if (Math.abs(height - lastPosted) < MIN_CHANGE) return true;
    lastPosted = height;
    postIframeLayout({
      mode: "auto-height",
      route: Routes.HomeScreen,
      height,
    });
    return true;
  };

  // The Home frame can still be hidden for a few frames while the previous
  // screen transitions out. Retry every frame until the first post lands.
  const publishSoon = () => {
    if (!active) return;
    if (publish() || Date.now() - startedAt > FIRST_POST_TIMEOUT_MS) return;
    frameId = requestAnimationFrame(publishSoon);
  };

  const onMessage = (event: MessageEvent) => {
    const height = reportedFrameHeight(event.data);
    if (!height || !event.source) return;
    const frame = Array.from(document.querySelectorAll("iframe")).find(
      (node) => node.contentWindow === event.source
    );
    if (!frame || isInactiveFrame(frame)) return;
    nestedHeight = height;
    writeCachedHeight(frame, height);
    publish();
  };

  window.addEventListener("message", onMessage);
  window.addEventListener("resize", publish);
  const checkId = window.setInterval(publish, 500);
  publishSoon();

  return () => {
    active = false;
    cancelAnimationFrame(frameId);
    window.removeEventListener("message", onMessage);
    window.removeEventListener("resize", publish);
    window.clearInterval(checkId);
  };
};

export const cleanupHomeHeight = (cleanup: () => void) => cleanup();
